// File: frontend/src/components/customTopics/SuggestCustomTopicModal.tsx

import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes, FaPaperPlane, FaHashtag, FaPlus, FaSearch } from "react-icons/fa";
import { customTopicService } from "../../services/customTopicService";
import { getPublicTopicCategories, MdTopicCategoryDto } from "../../services/masterDataService";
import { useAuth } from "../../context/AuthContext";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { parseApiError } from "../../utils/parseApiError";
import { InputClearButton } from "../common/InputClearButton";
import { formatCompactWithTitle } from "../../utils/numberFormatUtils";
import { normalizeSearchText } from "../../utils/searchUtils";
import "./SuggestCustomTopicModal.css";

// Typeahead fires from the 3rd character, debounced
const TYPEAHEAD_MIN_CHARS = 3;
const TYPEAHEAD_DEBOUNCE_MS = 300;
// Max channels ("kanal") per topic — mirrors backend CustomTopicService.MAX_CHANNELS_PER_TOPIC
const MAX_CHANNELS_PER_TOPIC = 10;
// Max channel name length — mirrors the md_topic_category.name column limit
const MAX_CHANNEL_NAME_LENGTH = 50;

// Normal channels are listed alphabetically — Turkish collation so ç/ğ/ı/ö/ş/ü sort
// in their proper place instead of after "z"
// Special channels (İstikram / Bırak system channels and the fixed "nickaltı"
// channel) always go to the bottom, alphabetical among themselves.
const NICKALTI_CHANNEL_NAME = "nickaltı"; // mirrors backend NickaltiTopicService.NICKALTI_CHANNEL_NAME
const isSpecialChannel = (c: MdTopicCategoryDto): boolean =>
  !!c.systemKey || c.name === NICKALTI_CHANNEL_NAME;
const sortChannelsByName = (list: MdTopicCategoryDto[]): MdTopicCategoryDto[] =>
  [...list].sort((a, b) => {
    const specialDiff = Number(isSpecialChannel(a)) - Number(isSpecialChannel(b));
    return specialDiff !== 0 ? specialDiff : a.name.localeCompare(b.name, "tr");
  });

interface SuggestCustomTopicModalProps {
  onClose: () => void;
  /**
   * Optional pre-filled topic title. Used by the search results page so a
   * failed search ("no results for X") can hand the keyword straight into
   * the suggestion form instead of making the user retype it.
   */
  initialTitle?: string;
}

/**
 * Modal for the "Başlık Öner" action in the left-pane "Gündem" tab.
 * Authenticated users submit a topic title suggestion (optionally with up to
 * 10 channels, hashtag-style); it lands in the admin panel where an admin/mod
 * approves (optionally edits title/channels) or rejects it. The selection
 * order of the channels is kept — the admin can reorder them while approving.
 *
 * Channels are picked via a searchable combobox: typing filters the active
 * channel list; when nothing matches, the user can propose a NEW channel name.
 * Proposed names render as dashed "new" chips and ride along with the
 * suggestion — the admin creates them (alphabetical master data position)
 * on approval if kept. Existing + proposed together share the 10-channel cap.
 *
 * Typeahead: from the 3rd character the input queries existing topics —
 * similar titles are listed and an exact duplicate blocks submission.
 *
 * The title can be pre-filled via `initialTitle` (search results page hands
 * over the keyword that returned nothing); the typeahead then runs on mount
 * exactly as if the user had typed it.
 */
export const SuggestCustomTopicModal: React.FC<SuggestCustomTopicModalProps> = ({ onClose, initialTitle }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  // Admin-configurable max title length (topic.title.max_length, clamped 50-80 by backend)
  const { topicTitleMaxLength } = useSiteSettings();

  // Topic titles are lowercase-only (sözlük convention) — normalize the
  // pre-filled value with the Turkish locale exactly like typed input
  const [title, setTitle]               = useState(() => (initialTitle ?? "").trim().toLocaleLowerCase("tr"));
  // Ordered channel ("kanal") selection — up to 10, selection order is kept
  // (index 0 becomes the primary channel; the user doesn't need to know that,
  // admins can reorder while approving)
  const [channelIds, setChannelIds]     = useState<number[]>([]);
  // NEW channel names proposed by the user (not in master data yet) — dashed
  // chips; created by the admin on approval. Shares the 10-channel cap with
  // channelIds.
  const [newChannelNames, setNewChannelNames] = useState<string[]>([]);
  // Channel combobox: free-text query + dropdown visibility
  const [channelQuery, setChannelQuery]             = useState("");
  const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
  // Closes the channel dropdown on outside clicks
  const channelBoxRef = useRef<HTMLDivElement | null>(null);
  const [categories, setCategories]     = useState<MdTopicCategoryDto[]>([]);
  // Active topic count per channel id — shown next to each dropdown option.
  // Channels without any active topic are absent from the endpoint → 0.
  const [channelCounts, setChannelCounts] = useState<Record<number, number>>({});
  const [error, setError]               = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted]       = useState(false);

  // Typeahead state
  const [matches, setMatches]         = useState<string[]>([]);
  const [exactExists, setExactExists] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against out-of-order typeahead responses (older query resolving last)
  const queryIdRef  = useRef(0);

  // Load active categories once (guests never see the form, skip for them)
  useEffect(() => {
    if (!isAuthenticated) return;
    getPublicTopicCategories()
      .then((list) => setCategories(sortChannelsByName(list)))
      .catch(() => {
        // Category dropdown is optional — fail silently, form still works
      });
    // Per-channel topic counts — purely informational, the dropdown works without them
    customTopicService.getTopicChannels()
      .then((rows) => {
        const map: Record<number, number> = {};
        rows.forEach((r) => { map[r.id] = r.count; });
        setChannelCounts(map);
      })
      .catch(() => {
        // Counts are optional — options simply show 0
      });
  }, [isAuthenticated]);

  // A pre-filled title (see `initialTitle`) can be longer than the
  // admin-configured limit. It is deliberately NOT truncated here: silently
  // cutting it would submit a mangled title the user never typed, and the
  // backend rejects anything over the limit anyway. Instead the over-limit
  // state is surfaced below and submission is blocked until the user shortens
  // it. Typed input is already capped by the input's maxLength.
  const titleTooLong = title.length > topicTitleMaxLength;

  // Close the channel dropdown when clicking anywhere outside the combobox.
  // Listens on "click" (NOT "mousedown") on purpose: the dropdown is rendered
  // in normal flow and pushes the footer down, so closing it on mousedown
  // collapsed the layout and moved the Gönder button out from under the
  // pointer BEFORE mouseup — the submit click never completed and the form
  // silently did nothing. With "click", the button's own onClick fires first,
  // then this handler closes the dropdown.
  useEffect(() => {
    const onDocumentClick = (e: MouseEvent) => {
      if (channelBoxRef.current && !channelBoxRef.current.contains(e.target as Node)) {
        setChannelDropdownOpen(false);
      }
    };
    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, []);

  // Debounced typeahead — fires from the 3rd character
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = title.trim();
    if (q.length < TYPEAHEAD_MIN_CHARS) {
      setMatches([]);
      setExactExists(false);
      return;
    }
    const queryId = ++queryIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await customTopicService.typeahead(q);
        // Ignore stale responses from earlier keystrokes
        if (queryId !== queryIdRef.current) return;
        setMatches(res.matches);
        setExactExists(res.exactExists);
      } catch {
        // Typeahead is a convenience — backend still enforces uniqueness on submit
      }
    }, TYPEAHEAD_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [title]);

  const handleSubmit = async () => {
    const trimmed = title.trim();
    if (!trimmed || exactExists || trimmed.length > topicTitleMaxLength) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await customTopicService.suggestTopic({
        title: trimmed,
        channelIds,
        newChannelNames,
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setError(parseApiError(msg, t));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSubmit();
  };

  // Existing + proposed channels share the single 10-channel cap
  const totalChannelCount = channelIds.length + newChannelNames.length;

  // Channel names are lowercase-only (sözlük convention) — Turkish locale
  // so dotted/dotless i is handled correctly
  const normalizedChannelQuery = channelQuery.trim().toLocaleLowerCase("tr");

  // Diacritic-blind version of the query for dropdown matching only:
  // "cografya" / "dunya" / "is dunyasi" find "coğrafya" / "dünya" / "iş dünyası".
  // normalizeSearchText folds ç ğ ı İ ö ş ü (and â î û etc.) on BOTH sides.
  // normalizedChannelQuery above stays untouched — it is still the exact
  // name used for the "suggest new channel" row.
  const foldedChannelQuery = normalizeSearchText(normalizedChannelQuery);

  // Dropdown list: active channels matching the query, minus already-picked ones
  const filteredCategories = categories.filter(
    (c) => !channelIds.includes(c.id) &&
      (!foldedChannelQuery || normalizeSearchText(c.name).includes(foldedChannelQuery))
  );

  // The "suggest new channel" row appears when the query matches neither an
  // existing channel nor an already-proposed name exactly
  const canProposeNewChannel =
    normalizedChannelQuery.length > 0 &&
    !categories.some((c) => c.name === normalizedChannelQuery) &&
    !newChannelNames.includes(normalizedChannelQuery);

  // Append a channel keeping the selection order; duplicates and overflow
  // beyond MAX_CHANNELS_PER_TOPIC are ignored (the dropdown hides them anyway)
  const addChannel = (id: number) => {
    setChannelIds((prev) =>
      prev.includes(id) || prev.length + newChannelNames.length >= MAX_CHANNELS_PER_TOPIC
        ? prev : [...prev, id]
    );
    setChannelQuery("");
  };

  const removeChannel = (id: number) => {
    setChannelIds((prev) => prev.filter((c) => c !== id));
  };

  // Propose a NEW channel name from the current query — dashed chip
  const addNewChannelName = () => {
    if (!canProposeNewChannel || totalChannelCount >= MAX_CHANNELS_PER_TOPIC) return;
    setNewChannelNames((prev) => [...prev, normalizedChannelQuery]);
    setChannelQuery("");
  };

  const removeNewChannelName = (name: string) => {
    setNewChannelNames((prev) => prev.filter((n) => n !== name));
  };

  return (
    <div className="suggest-topic-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="suggest-topic-modal">
        <div className="suggest-topic-header">
          <h3>
            <FaHashtag size={13} />
            {t("suggest_topic_title", "Yeni Başlık Öner")}
          </h3>
          <button className="suggest-topic-close" onClick={onClose} aria-label={t("admin_cancel", "İptal")}>
            <FaTimes />
          </button>
        </div>

        <div className="suggest-topic-body">
          {!isAuthenticated ? (
            // Guests cannot suggest topics — show the login prompt instead of the form
            <p className="suggest-topic-login-required">
              {t("suggest_topic_login_required", "Başlık önermek için giriş yapmalısın.")}
            </p>
          ) : submitted ? (
            // Success state — suggestion is now waiting for admin review
            <p className="suggest-topic-success">
              {t("suggest_topic_success", "Önerin alındı! Admin onayından sonra yayınlanacak.")}
            </p>
          ) : (
            <>
              <p className="suggest-topic-info">
                {t("suggest_topic_info", "Önerin admin onayından sonra yayınlanır.")}
              </p>
              {error && <div className="suggest-topic-error">{error}</div>}
              {/* Wrapper only exists to give the clear button a positioning context */}
              <div className="suggest-topic-title-field">
                <input
                  type="text"
                  value={title}
                  // Topic titles are lowercase-only (sözlük convention) — Turkish
                  // locale so dotted/dotless i is handled correctly
                  onChange={(e) => { setTitle(e.target.value.toLocaleLowerCase("tr")); if (error) setError(null); }}
                  onKeyDown={handleKeyDown}
                  placeholder={t("suggest_topic_placeholder", { max: topicTitleMaxLength })}
                  maxLength={topicTitleMaxLength}
                  autoFocus
                />
                {title !== "" && (
                  <InputClearButton onClear={() => { setTitle(""); if (error) setError(null); }} />
                )}
              </div>
              <div className="suggest-topic-char-counter">
                {title.length} / {topicTitleMaxLength}
              </div>

              {/* Over the configured limit (only reachable via a pre-filled
                  title) → warn and block submission until it is shortened */}
              {titleTooLong && (
                <div className="suggest-topic-duplicate-warning">
                  {t("suggest_topic_too_long", { max: topicTitleMaxLength })}
                </div>
              )}

              {/* Exact duplicate → warning; otherwise list similar existing titles */}
              {exactExists ? (
                <div className="suggest-topic-duplicate-warning">
                  {t("suggest_topic_exact_exists", "Bu başlık zaten mevcut ya da onay bekliyor.")}
                </div>
              ) : matches.length > 0 && (
                <div className="suggest-topic-matches">
                  <div className="suggest-topic-matches-label">
                    {t("suggest_topic_similar", "Benzer başlıklar:")}
                  </div>
                  {matches.map((m) => (
                    <div key={m} className="suggest-topic-match">{m}</div>
                  ))}
                </div>
              )}

              {/* Optional channels ("kanal") — hashtag semantics, up to 10.
                  Chips keep the selection order. Channels are added via a
                  searchable combobox; a query matching nothing can be
                  proposed as a NEW channel (dashed chip, created by the
                  admin on approval). */}
              <div className="suggest-topic-category">
                <label>{t("topic_channels_label", "Kanallar")}</label>
                <div className="suggest-topic-channel-area">
                  {(channelIds.length > 0 || newChannelNames.length > 0) && (
                    <div className="suggest-topic-channel-chips">
                      {channelIds.map((id) => {
                        const cat = categories.find((c) => c.id === id);
                        if (!cat) return null;
                        return (
                          <span key={id} className="suggest-topic-channel-chip">
                            {cat.name}
                            <button
                              type="button"
                              className="suggest-topic-channel-remove"
                              onClick={() => removeChannel(id)}
                              title={t("topic_channel_remove", "Kaldır")}
                            >
                              <FaTimes size={9} />
                            </button>
                          </span>
                        );
                      })}
                      {/* Proposed NEW channels — dashed chips with a "yeni" badge */}
                      {newChannelNames.map((name) => (
                        <span key={name} className="suggest-topic-channel-chip suggest-topic-channel-chip--new">
                          {name}
                          <span className="suggest-topic-channel-new-badge">
                            {t("topic_channel_new_badge", "yeni")}
                          </span>
                          <button
                            type="button"
                            className="suggest-topic-channel-remove"
                            onClick={() => removeNewChannelName(name)}
                            title={t("topic_channel_remove", "Kaldır")}
                          >
                            <FaTimes size={9} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {totalChannelCount < MAX_CHANNELS_PER_TOPIC ? (
                    /* The wrapper carries the outside-click ref so clicks on the
                       dropdown itself never count as "outside" (the document
                       "click" listener above ignores targets inside this box) */
                    <div className="suggest-topic-channel-picker" ref={channelBoxRef}>
                      <div className="suggest-topic-channel-search">
                        <FaSearch size={10} className="suggest-topic-channel-search-icon" />
                        <input
                          type="text"
                          value={channelQuery}
                          // Channel names are lowercase-only (sözlük convention)
                          onChange={(e) => {
                            setChannelQuery(e.target.value.toLocaleLowerCase("tr"));
                            setChannelDropdownOpen(true);
                          }}
                          onFocus={() => setChannelDropdownOpen(true)}
                          onKeyDown={(e) => {
                            // Enter proposes the typed name when nothing matches it exactly —
                            // and must never submit the whole form from this input
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                              if (canProposeNewChannel && filteredCategories.length === 0) addNewChannelName();
                            }
                          }}
                          placeholder={t("topic_channel_search_placeholder", "kanal ara ya da yeni öner...")}
                          maxLength={MAX_CHANNEL_NAME_LENGTH}
                        />
                        {channelQuery !== "" && (
                          <InputClearButton onClear={() => setChannelQuery("")} />
                        )}
                      </div>
                      {/* Rendered in normal flow (NOT absolute) — the modal has
                          overflow:hidden, so an absolute dropdown would be
                          clipped at the modal edge. In-flow it pushes the modal
                          body down and stays fully visible. */}
                      {channelDropdownOpen && (channelQuery.trim().length > 0 || filteredCategories.length > 0) && (
                        <div className="suggest-topic-channel-dropdown">
                          {filteredCategories.map((c) => {
                            // Compact topic count ("1,2k"), exact value as tooltip when shortened
                            const count = formatCompactWithTitle(channelCounts[c.id] ?? 0);
                            return (
                              <button
                                key={c.id}
                                type="button"
                                className="suggest-topic-channel-option"
                                onClick={() => addChannel(c.id)}
                              >
                                <span className="suggest-topic-channel-option-name">#{c.name}</span>
                                <span className="suggest-topic-channel-option-count" title={count.title}>
                                  {count.text}
                                </span>
                              </button>
                            );
                          })}
                          {filteredCategories.length === 0 && !canProposeNewChannel && (
                            <div className="suggest-topic-channel-empty">
                              {t("topic_channel_no_results", "kanal bulunamadı")}
                            </div>
                          )}
                          {canProposeNewChannel && (
                            <button
                              type="button"
                              className="suggest-topic-channel-option suggest-topic-channel-option--new"
                              onClick={addNewChannelName}
                            >
                              <FaPlus size={9} />
                              {t("topic_channel_suggest_new", { name: normalizedChannelQuery })}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="suggest-topic-channel-max">
                      {t("topic_channel_max_reached", { max: MAX_CHANNELS_PER_TOPIC })}
                    </div>
                  )}
                  {/* Proposed new channels are only created after admin review */}
                  {newChannelNames.length > 0 && (
                    <div className="suggest-topic-channel-proposed-hint">
                      {t("topic_channel_proposed_hint", "Yeni kanal önerileri admin onayından sonra oluşturulur.")}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="suggest-topic-footer">
          {submitted || !isAuthenticated ? (
            <button className="suggest-topic-cancel-btn" onClick={onClose}>
              {t("close_button", "Kapat")}
            </button>
          ) : (
            <>
              <button className="suggest-topic-cancel-btn" onClick={onClose}>
                {t("admin_cancel", "İptal")}
              </button>
              <button
                className="suggest-topic-submit-btn"
                onClick={handleSubmit}
                disabled={isSubmitting || !title.trim() || exactExists || titleTooLong}
              >
                <FaPaperPlane size={11} />
                {t("suggest_topic_submit", "Gönder")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};